import { useCallback } from 'react';
import { useTranslations } from 'use-intl';
import useCart from '@/lib/hooks/useCart';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { pushAddToCart } from '@/lib/analytics/dataLayer';
import { trackConvert } from '@/lib/algolia/insights';
import toast from '@/components/atoms/toaster/helpers/toast';
import { Product } from '@/types/entity/product';

interface PdpInsightsBridge {
  // EWM-809 fix-wave A — returns the full attribution object (queryID + originating
  // index, which may be a replica) so both the BFF stamp and the convert event use
  // the correct index. Returns undefined when the product was not found via search.
  getSearchAttribution: () => { queryID: string; index: string } | undefined;
  algoliaIndex: string;
}

const usePDPCart = (product: Product, insights?: PdpInsightsBridge) => {
  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();
  const { addItem } = useCart(selectedBusinessUnit?.key, selectedStore?.key);
  const translate = useTranslations();

  const addToCart = useCallback(
    async (count: number) => {
      const sku = product.sku ?? '';
      const attr = insights?.getSearchAttribution();
      const result = await addItem([
        {
          sku,
          count,
          ...(attr ? { algoliaQueryId: attr.queryID, algoliaIndex: attr.index } : {}),
        },
      ]);
      if (!result.success) {
        const message = (result as { message?: string }).message || translate('common.something-went-wrong');
        toast.error(message, { position: 'top-right' });
        return;
      }
      // EWM-434 — success toast (formerly in main-info.tsx handleOnAddToCart).
      toast.success(translate('cart.item-added'), { position: 'top-right' });
      // EWM-809 — Algolia add-to-cart (search-attributed when attribution was recalled).
      // Uses attr.index (replica) when present; falls back to bridge's primary index.
      // EWM-980 — attach objectData (price + currency) from the resolved cart line.
      // CT line `price.value` is NET / ex-GST (Electus stores trade prices
      // GST-exclusive, includedInPrice=false — GST is charged on top), which is
      // the net ex-tax price the AC asks for, not the RRP/list price.
      if (insights) {
        const cart = 'lineItems' in result ? result : undefined;
        const unitNetCents = cart?.lineItems?.find((li) => li.variant?.sku === sku)?.price?.centAmount;
        const currency = cart?.sum?.currencyCode ?? product.currency;
        trackConvert({
          index: attr ? attr.index : insights.algoliaIndex,
          objectIDs: [sku],
          eventName: 'Add to cart (PDP)',
          ...(attr ? { queryID: attr.queryID } : {}),
          ...(unitNetCents != null
            ? { objectData: [{ price: unitNetCents / 100, quantity: count }], ...(currency ? { currency } : {}) }
            : {}),
        });
      }
      pushAddToCart({
        currency: product.currency,
        items: [
          {
            item_id: product.sku ?? product.id,
            item_name: product.name,
            price: product.price,
            quantity: count,
            item_brand: product.brand,
          },
        ],
      });
    },
    [addItem, product, insights, translate],
  );

  return { addToCart };
};

export default usePDPCart;
